import { Component, ViewChild } from '@angular/core';
import { IonicPage, NavController, NavParams, Tabs } from 'ionic-angular';
import { RouteProvider } from '../../providers/route/route';
import { OrderProvider } from '../../providers/order/order';
import { INavInfo } from '../../model/models/layout/nav-info.interface';
import { SegmentProvider } from '../../providers/analytics/segment';
import { AnalyticsEventType, AnalyticsProfileCreationStartedLocations, SegmentLocationNames } from '../../model/types/analytics.const';
import { LoggerProvider, LoggerEvent } from '../../providers/logger/logger';
import { OnfidoProvider } from '../../providers/myria/onfido-provider';
import { LoadingProvider } from '../../providers/loading/loading';
import { APP_CONFIG, Region } from '../../environment';
import { WalletProvider } from '../../providers/wallet/wallet';
import { LoginProvider } from '../../providers/myria/login-provider';
import { TranslateService } from '@ngx-translate/core';
import { StorageProvider } from '../../providers/storage/storage';
import { StorageType } from '../../model/types/storage-type';
import { SendFlowProvider } from '../../providers/send-flow/send-flow';
import { AuthProvider } from '../../providers/authorization/auth';
import { DeeplinkProvider } from '../../providers/marketing/deeplink';
import { PinActionType } from '../../model/types/wallet-pin-action-type';
import { PROMOTION_TYPE } from '../../providers/myria/app-variables';
import { CustomerProvider } from '../../providers/customer/customer';
import { RmtPopupComponent } from "../../components/rmt-popup/rmt-popup";
import { FeatureFlagTypes, UtilsProvider } from '../../providers/myria/utils-provider';
import { ColorProvider } from '../../providers/color/color';
import { CommonProvider } from '../../providers/common/common';
import { WalletCardProvider } from '../../providers/wallet/wallet-card';
import { HomeScreenProvider } from '../../providers/homescreen/homescreen';
import { VueProvider } from '../../providers/vue/vue';
import { DingTopUpProvider } from '../../providers/ding-top-up/ding-top-up';
import { ITabMoreItem } from '../../model/models/layout/tab-more-item.interface';

// The values that the More sheet returns. Each row has a different value.
enum MoreItem {
  Locations = 1,
  TrackTransfer = 2,
  MobileTopUp = 3,
  ActivateWallet = 4,
}

@IonicPage()
@Component({
  selector: 'page-tabs-home',
  templateUrl: 'tabs-home.html',
})
export class TabsHomePage {
  @ViewChild('tabControl') tabControl: Tabs;
  page: string = 'Page';
  tabs: INavInfo[];
  selectedTab: INavInfo;
  lastLocation: string = 'Activity';
  walletEnabled: boolean = false;
  isHubEnabled: boolean = false;
  isModalOpened: boolean = false;
  deepLinkMarketingSubscribe;
  deepLinkMarketTransactBackgroundSubscribe;
  rootParams;
  initPromise;
  showStoreLocatorEmbedded: boolean = false;
  showMobileTopUp = false;
  walletStatusString: string;
  walletDetails; 
  // MY/SN wallet holder on the native wallet: the US pilot wallet lives in the Vue bridge and
  // must not touch the legacy wallet APIs or the wallet send sheet.
  private get isLegacyWalletHolder(): boolean {
    return this.walletEnabled && this.walletProvider.hasWallet() && !this.walletProvider.isWalletSupportEnabled();
  }
  isProcessingAction: boolean = false;
  private hasAutoOpenedWalletOnboarding: boolean = false;

  constructor(
    public navCtrl: NavController,
    private navParams: NavParams,
    private orderProvider: OrderProvider,
    private onfidoProvider: OnfidoProvider,
    private routeProvider: RouteProvider,
    private loadingProvider: LoadingProvider,
    private analytics: SegmentProvider,
    private walletProvider: WalletProvider,
    private loginProvider: LoginProvider,
    private translate: TranslateService,
    private storageProvider: StorageProvider,
    private sendFlowProvider: SendFlowProvider,
    private authProvider: AuthProvider,
    private deeplinkProvider: DeeplinkProvider,
    private customerProvider: CustomerProvider,
    private utilsProvider: UtilsProvider,
    private commonProvider: CommonProvider,
    private walletCardProvider: WalletCardProvider,
    private homescreenProvider: HomeScreenProvider,
    private vueProvider: VueProvider,
    private dingTopUpProvider: DingTopUpProvider,
  ) {
    UtilsProvider.tabs = this;
    this.initPromise = this.init();
    if(!this.deepLinkMarketingSubscribe){
      this.deepLinkMarketingSubscribe = this.deeplinkProvider.deeplinkOptInMarketingNotifications.subscribe((value) => {
        if(value === true || value === "true" || value === 'true'){
          this.optInMarketingNotifications();
        }
      });
    }
    if(!this.deepLinkMarketTransactBackgroundSubscribe){
      this.deepLinkMarketTransactBackgroundSubscribe = this.deeplinkProvider.deeplinkOptInMarketTransactNotifications.subscribe((value) => {
        if(value === true || value === "true" || value === 'true'){
          this.optInMarketTransactNotifications();
        }
      });
    }
  }

  selectTab(tab) {
    for (let i = 0; i < this.tabs.length; i++) {
      if (this.tabs[i].page === tab.page) {
        if (this.tabControl)
          this.tabControl.select(i);
        else
          LoggerProvider.info(this, 'Missing tab control', tab);
        return true;
      }
    }
    LoggerProvider.info(this, 'Did not find tab', tab);
    return false;
  }

  async init(){
    await this.utilsProvider.ensureFeatureFlags();
    this.walletEnabled = await this.walletProvider.isEnabled();
    const walletSupportEnabled = this.walletProvider.isWalletSupportEnabled();
    this.showStoreLocatorEmbedded = await this.utilsProvider.hasFeatureFlag(FeatureFlagTypes.StoreLocatorEmbedded);
    this.showMobileTopUp = await this.dingTopUpProvider.isAvailable();

    this.tabs = [];
    if (this.walletEnabled) {
      if (walletSupportEnabled) {
        // US wallet pilot: the wallet UX is the Vue bridge, so the native root under it never
        // mounts the MY/SN WalletHomePage (it flashes through the slide-in and calls the legacy
        // wallet APIs). Holders keep the activity-first layout; until they have a wallet the
        // onboarding tab replaces the recipient list.
        this.tabs.push(this.routeProvider.pages.ActivityPage);
        if (this.walletProvider.hasWallet()) {
          this.tabs.push(this.routeProvider.pages.RecipientListPage);
          this.tabs.push(this.routeProvider.pages.SendPage);
          this.tabs.push(this.routeProvider.pages.TrackTransferPage);
          this.tabs.push(this.routeProvider.pages.TabMorePage);
        } else {
          this.tabs.push(this.routeProvider.pages.WalletOnboardingTab);
          this.tabs.push(this.routeProvider.pages.SendPage);
          this.tabs.push(this.routeProvider.pages.TrackTransferPage);
          this.tabs.push(this.routeProvider.pages.TabMoreNonActivatedWalletPage);
        }
      } else if (this.walletProvider.hasWallet()) {
        this.tabs.push(this.routeProvider.pages.WalletHome);
        this.loadWalletDetails();
        this.tabs.push(this.routeProvider.pages.ActivityPage);
        this.tabs.push(this.routeProvider.pages.SendPage);
        this.tabs.push(this.routeProvider.pages.RecipientListPage);
        this.tabs.push(this.routeProvider.pages.TabMorePage);
      } else {
        this.tabs.push(this.routeProvider.pages.ActivityPage);
        this.tabs.push(this.routeProvider.pages.RecipientListPage);
        this.tabs.push(this.routeProvider.pages.SendPage);
        this.tabs.push(this.routeProvider.pages.TrackTransferPage);
        this.tabs.push(this.routeProvider.pages.TabMoreNonActivatedWalletPage);
      }

    } else {
      this.tabs.push(this.routeProvider.pages.ActivityPage);
      this.tabs.push(this.routeProvider.pages.RecipientListPage);
      this.tabs.push(this.routeProvider.pages.SendPage);
      this.tabs.push(this.routeProvider.pages.TrackTransferPage);
      if (this.isHubEnabled)
        this.tabs.push(this.routeProvider.pages.HubPage);
      this.tabs.push(this.getLastTabWithoutWallet());
    }
    this.selectedTab = this.tabs[0];
  }

  private get isLocationsTabReplaced(): boolean {
    return !this.walletEnabled && this.showMobileTopUp;
  }

  // Without a Locations tab, the embedded store locator has no place to show, so open the store locator page.
  private handleDeepLinkLocations() {
    if (this.isLocationsTabReplaced)
      this.handleLocationsNavigation(true);
    else
      this.handleLocationsNavigation();
  }

  private getLastTabWithoutWallet(): INavInfo {
    if (this.showMobileTopUp)
      return this.routeProvider.pages.TabMorePage;
    if (this.showStoreLocatorEmbedded)
      return this.routeProvider.pages.StoresEmbeddedPage;
    return this.routeProvider.pages.StoresPage;
  }

  async ionViewDidEnter() {
    this.isProcessingAction = false;

    if (this.vueProvider && this.vueProvider.isSendMoneyFromBridge) {
      this.vueProvider.returnToBridgeFromSendMoney();
      return;
    }

    await this.initPromise;
    if (this.isLegacyWalletHolder) {
      this.walletDetails = await this.walletProvider.getWalletDetails();
      this.walletStatusString = await this.walletProvider.getWalletStatusInString(this.walletDetails);
      this.logWalletAccessed();
    }

    await this.loginProvider.isProfileCompleted();

    if (!this.hasAutoOpenedWalletOnboarding
        && !this.walletProvider.onboardingDismissed
        && this.walletEnabled
        && this.walletProvider.isWalletSupportEnabled()
        && this.loginProvider.isLiteUser()
        && this.loginProvider.isProfileCompleted() 
        && !this.walletProvider.hasWallet()) {
      this.hasAutoOpenedWalletOnboarding = true;
      this.vueProvider.navigateWithFreshSession('WalletOnboarding');
    }

    const tab = this.navParams.get('tab');
    if (tab) {
      for (let i = 0; i < this.tabs.length; i++) {
        if (this.tabs[i].page === tab.page) {
          this.tabControl.select(i);
          break;
        }else if (this.tabs[i].page === this.routeProvider.pages.TabMorePage.page || this.tabs[i].page === this.routeProvider.pages.HubPage.page || this.tabs[i].page === this.routeProvider.pages.StoresPage.page || this.tabs[i].page === this.routeProvider.pages.StoresEmbeddedPage.page){
          if (tab.page == this.routeProvider.pages.StoresPage.page) {
            this.handleDeepLinkLocations();
          } else if (tab.page == this.routeProvider.pages.StoresEmbeddedPage.page) {
            this.handleDeepLinkLocations();
          } else if (tab.page == this.routeProvider.pages.TrackTransferPage.page){
            this.handleTrackTransferNavigation();
          }
        }
      }
    }
  }

  rmtViewDidReEnter() {
    this.isProcessingAction = false;

    // Send Money flow originated in the Vue bridge — bounce the user back
    // into the bridge (RMT) instead of staying on a OneApp tab.
    if (this.vueProvider && this.vueProvider.isSendMoneyFromBridge) {
      this.vueProvider.returnToBridgeFromSendMoney();
      return;
    }

    if (this.tabControl && this.tabControl.selectedIndex && this.tabControl._tabs[this.tabControl.selectedIndex]) {
      const tab = this.tabControl._tabs[this.tabControl.selectedIndex];
      if (tab && tab._views && tab._views.length > 0 && tab._views[0] && tab._views[0].instance && tab._views[0].instance.rmtViewDidReEnter)
        tab._views[0].instance.rmtViewDidReEnter();
    }
  }

  async loadWalletDetails() {
    // TODO remove ?
    if(!this.walletProvider.isWalletBalanceLoadedLogged){
      this.analytics.trackEvent({ eventName: AnalyticsEventType.WalletBalanceLoaded, analyticsData: {} });
      this.walletProvider.isWalletBalanceLoadedLogged = true;
    }
  }

  ionViewDidLeave() {
    const tab = this.tabControl.getSelected();
    if (tab && tab._views && tab._views.length > 0 && tab._views[0].instance && tab._views[0].instance.ionViewDidLeave)
      tab._views[0].instance.ionViewDidLeave();
  }


  async openSendMoney() {
    const storedOrder = await this.orderProvider.getStoredOrder();
    if (storedOrder && storedOrder.activityDetails && storedOrder.activityDetails.page) {
      if (storedOrder.pendingEKyc || storedOrder.onfidoId) {
        this.loadingProvider.show();
        try {
          this.onfidoProvider.checkId = storedOrder.onfidoId;
          const res = await this.onfidoProvider.getOnfidoStatus(false);
          this.onfidoProvider.handleResult(res, storedOrder, true);
          this.loadingProvider.hide();
          return;
        }
        catch (ex) {
          LoggerProvider.exception(this, ex, LoggerEvent.PageException, 'Failed to get Onfido status in send money');
        }
        this.loadingProvider.hide();
      }
      this.routeProvider.openModal(storedOrder.activityDetails.page);
    }
    else {
      this.sendFlowProvider.sendMoneyBtn();
    }
  }

  tabChanges(event) {
    if (this.isMoreTab(event)) {
      this.openMoreActionSheet();
      this.routeProvider.logEnter({ page: this.routeProvider.pages.TabMorePage.page }, {}, 'Change Tab');
      return;
    }
    this.setTabStyles(event);
    if (event.root === this.routeProvider.pages.ActivityPage.page) {
      this.lastLocation = 'Activity'
    } else if (event.root === this.routeProvider.pages.SendPage.page) {
      // this.openSendMoney();
      this.onClickSendMoneyButton();
    }
    else if (event.root === this.routeProvider.pages.TrackTransferPage.page) {
      this.goBackToPreviousTab();
      this.handleTrackTransferNavigation();
    }
    else if (event.root === this.routeProvider.pages.TabSendMorePage.page) {
      this.commonProvider.openSendActionSheet(this.walletDetails, this.lastLocation);
    }
    else if (event.root === this.routeProvider.pages.StoresPage.page) {
      this.goBackToPreviousTab();
      this.handleLocationsNavigation();
    } else if (event.root === this.routeProvider.pages.StoresEmbeddedPage.page) {
      this.handleLocationsNavigation();
    } else if (event.root === this.routeProvider.pages.TabHubPage.page) {
      this.goBackToPreviousTab();
      this.handleHubNavigation();
    } else if (event.root === this.routeProvider.pages.WalletOnboardingTab.page) {
      this.goBackToPreviousTab();
      this.vueProvider.navigateWithFreshSession('WalletOnboarding');
    } else if(event.root === this.routeProvider.pages.WalletHome.page){
      this.lastLocation = 'Wallet Home';
      if (!this.isModalOpened && this.walletEnabled) {
        if (this.walletProvider.hasWallet()) {
          this.logWalletAccessed();
        } else {
          this.analytics.trackEvent({ eventName: AnalyticsEventType.WalletAccessed, analyticsData: { isWalletActivated: false } });
        }
      } else {
        this.isModalOpened = false;
      }
    }
    else if(event.root == null) this.checkLiteUser();
    this.routeProvider.logEnter({ page: event.root }, {}, 'Change Tab');
  }

  async goBackToPreviousTab() {
    this.isModalOpened = true;
    let tabPrv = this.tabControl.previousTab(false);
    if (tabPrv) {
      this.analytics.fireAccessEvent = false;
      await this.tabControl.select(tabPrv.index);
    }
  }

  // The More tab has no root page (see getRootPage). Thus Ionic does not select it, and the change event has no root to identify it.
  private isMoreTab(event): boolean {
    const tab = this.tabs[event.index];
    return !!tab && tab.page === this.routeProvider.pages.TabMorePage.page;
  }

  async openMoreActionSheet() {
    const locationsItem: ITabMoreItem = { value: MoreItem.Locations, icon: 'assets/icons/location-more.svg', textKey: 'global.mainNavigation.locationsButton' };
    if (!this.walletEnabled) {
      this.openMoreSheet(this.routeProvider.pages.TabMorePage, [locationsItem], (item) => {
        if (item === MoreItem.Locations) {
          // This tab bar has no Locations tab. Thus the store locator opens as a page, also when the embedded store locator is on.
          this.handleLocationsNavigation(true);
        }
      });
    } else if (!this.walletProvider.hasWallet()) {
      // Both wallets push TabMoreNonActivatedWalletPage in this state (US pilot and
      // MY/SN), so this must not narrow to the US flag or MY/SN gets the wrong sheet.
      const activateWalletItem: ITabMoreItem = { value: MoreItem.ActivateWallet, icon: 'assets/icons/wallet-tab-more.svg', textKey: 'page.activityPage.activateYourWalletText' };
      this.openMoreSheet(this.routeProvider.pages.TabMoreNonActivatedWalletPage, [locationsItem, activateWalletItem], (item) => {
        if (item === MoreItem.Locations) {
          this.handleLocationsNavigation();
        } else if (item === MoreItem.ActivateWallet) {
          this.handleActivateWalletNavigation();
        }
      });
    } else {
      const trackTransferItem: ITabMoreItem = { value: MoreItem.TrackTransfer, icon: 'assets/icons/geolocation.svg', textKey: 'global.mainNavigation.trackTransferButton' };
      this.openMoreSheet(this.routeProvider.pages.TabMorePage, [locationsItem, trackTransferItem], (item) => {
        if (item === MoreItem.Locations) {
          this.handleLocationsNavigation();
        } else if (item === MoreItem.TrackTransfer) {
          this.handleTrackTransferNavigation();
        }
      });
    }
  }

  private openMoreSheet(page: INavInfo, items: ITabMoreItem[], onSelect: (item: MoreItem) => void) {
    const mobileTopUpItems: ITabMoreItem[] = this.showMobileTopUp
      ? [{ value: MoreItem.MobileTopUp, icon: 'assets/icons/device-phone-mobile.svg', textKey: 'global.mainNavigation.more.mobileTopUps' }]
      : [];
    // The More sheet changes no data. Thus the home page must not load its data again when the sheet closes.
    const reEnterOnDismiss = false;
    this.routeProvider.openActionSheet(page, { items: [...mobileTopUpItems, ...items] }, (item: MoreItem) => {
      if (item === MoreItem.MobileTopUp) {
        this.openMobileTopUpFromMore();
      } else {
        onSelect(item);
      }
    }, 'rmt-select-actionsheet-modal', false, false, reEnterOnDismiss);
  }

  openMobileTopUpFromMore() {
    this.analytics.trackEvent({
      eventName: AnalyticsEventType.ElementClicked,
      analyticsData: { elementId: 'more_mobile_top_up', elementType: 'list_item', area: 'navigation.more' }
    });
    this.dingTopUpProvider.start();
  }

  async onClickSendMoneyButton() {
    if (this.isProcessingAction) return;
    this.isProcessingAction = true;
    if (this.loginProvider.isUserFraudBlocked()) {
      return this.utilsProvider.redirectToRestrictedTransfers();
    }

    if (this.isLegacyWalletHolder)
      this.commonProvider.openSendActionSheet(this.walletDetails, this.lastLocation);
    else
      this.openSendMoney();
  }

  openSendOrRequestClick(){
    this.routeProvider.openModal(this.routeProvider.pages.RecipientListPage, { actionType: PinActionType.SendOrRequest, location: this.lastLocation });
  }

  handleLocationsNavigation(openAsPage: boolean = !this.showStoreLocatorEmbedded) {
    this.analytics.trackEvent({ eventName: AnalyticsEventType.FindALocationAccessed, analyticsData: { location: this.lastLocation, embedded: !openAsPage } });
    this.homescreenProvider.setHomeScreenLocation(SegmentLocationNames.FindALocation);
    if (APP_CONFIG.featureFlags.findLocationRevamp && openAsPage)
      this.routeProvider.openModal(this.routeProvider.pages.StoresPageNew, null, null, 'store-locator-modal');
  }

  handleHubNavigation() {
    this.analytics.trackEvent({ eventName: AnalyticsEventType.HubPageAccessed, analyticsData: { location: this.lastLocation } });
    this.lastLocation = 'Hub';
    if (APP_CONFIG.featureFlags.findLocationRevamp)
      this.routeProvider.openModal(this.routeProvider.pages.HubPage);
  }

  handleTrackTransferNavigation(){
    this.analytics.trackEvent({ eventName: AnalyticsEventType.TrackTransferAccessed, analyticsData: { location: 'navbar' } });
    this.homescreenProvider.setHomeScreenLocation(SegmentLocationNames.TrackATransfer);
    // this.routeProvider.setRoot(this.routeProvider.pages.TrackTransferPage);
    this.routeProvider.openModal(this.routeProvider.pages.TrackTransferPage, null, null, 'store-locator-modal');
  }
  async handleActivateWalletNavigation(){
    this.walletProvider.setIsWalletActivateFromSMF(false);
    this.walletProvider.getWalletActivate('More');
  }

  checkLiteUser() {
    if(this.loginProvider.isLiteUser()) {
      this.openLiteUserModal();
    }
  }

  openLiteUserModal() {
    this.utilsProvider.openLiteRegActionSheetPage(this.routeProvider.pages.RecipientListPage, AnalyticsProfileCreationStartedLocations.Recipients);
  }

  getRootPage(tab: INavInfo) {
    let page = tab.page;
    if(page == this.routeProvider.pages.RecipientListPage.page && this.loginProvider.isLiteUser()) {
      page = null;
    }
    // The More tab only opens the More sheet. A tab without a root page does not leave the current tab, so the current tab does not load again.
    if (page === this.routeProvider.pages.TabMorePage.page) {
      page = null;
    }

    return page;
  }

  async optInMarketTransactNotifications() {
    try {

      this.loadingProvider.show();
      await this.customerProvider.callNotificationSubscriptions(true)

      if (AuthProvider.isLoggedIn()) {
        let customer = await this.customerProvider.getCustomer();
        customer.willReceivePushNotifications = true;
        customer.willReceivePushMarketing = true;
        customer.willReceiveEmailMarketing = true;
        await this.customerProvider.updateCustomer(customer);
      }
      this.loadingProvider.hide();
      this.deeplinkProvider.deeplinkOptInMarketTransactNotifications.next(false);
      RmtPopupComponent.showSuccess(this.translate.instant('global.marketingNotificationsOn'));
      this.routeProvider.popTo(this.routeProvider.pages.SettingsNotificationsPage);
    } catch (ex) {
      this.loadingProvider.hide();
      LoggerProvider.exception(this, ex, LoggerEvent.PageException, 'failed optInMarketingNotifications');
    }
  }

  async optInMarketingNotifications() {
    try {
      if(AuthProvider.isLoggedIn()) {
        this.loadingProvider.show();

        await this.customerProvider.setMarketingNotifications();
        this.customerProvider.setMarketingPushNotificationsOpted(true);

        this.loadingProvider.hide();

        this.routeProvider.push(this.routeProvider.pages.SettingsNotificationsPage);
        RmtPopupComponent.showSuccess(this.translate.instant('global.marketingNotificationsOn'));
      }
    } catch (ex) {
      this.loadingProvider.hide();
      LoggerProvider.exception(this, ex, LoggerEvent.PageException, 'failed optInMarketingNotifications');
    }
  }

  ngOnDestroy() {
    this.deepLinkMarketingSubscribe.unsubscribe();
    this.deepLinkMarketTransactBackgroundSubscribe.unsubscribe();
  }

  setTabStyles(event){
    if(APP_CONFIG.region.region === Region.MY){
      for(let tab of this.tabControl._tabs){
        this.setTabColor("", tab._btnId);
      }
      const color = ColorProvider.getColorValue('--app-primary');
      this.setTabColor(color, event._btnId);
    }
  }

  setTabColor(color, buttonId){
    let tab = document.getElementById(buttonId);
    if(tab){
      let tabTextSpan = tab.querySelector("span");
      if(tabTextSpan){
        tabTextSpan.style.setProperty("color", color, "important");
      }
      let tabIcon: any = tab.querySelector("ion-icon");
      if(tabIcon){
        tabIcon.style.setProperty("color", color, "important");
      }
    }
  }

  logWalletAccessed() {
    // Used to log wallet accessed initially as QA reported event isn't logged initially, subsequent events are handled by tabchanges()
    let activeCards;
    if (this.walletDetails && this.walletDetails.Cards) activeCards = this.walletCardProvider.getActiveCardsForSegmentEv(this.walletDetails.Cards);
    this.analytics.trackEvent({ eventName: AnalyticsEventType.WalletAccessed, analyticsData: {
      isWalletActivated: true,
      country: APP_CONFIG.region.defaultCountryFrom,
      walletStatus: this.walletStatusString,
      walletBalance: this.walletDetails ? this.walletDetails.WalletAvailableBalance : '',
      walletCards: activeCards
    }});
  }
}



// WEBPACK FOOTER //
// ./src/pages/tabs-home/tabs-home.ts